/**
 * Global Axios Configuration
 * Handles errors, retries, and timeouts globally
 */

import axios from 'axios';
import { toast } from 'sonner';

// Request interceptor
axios.interceptors.request.use(
  (config) => {
    // Set default timeout if not specified
    if (!config.timeout) {
      config.timeout = 30000; // 30 seconds default
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Network error (no response from server)
    if (!error.response) {
      console.error('Network error:', error.message);
      
      // Retry logic for network errors (max 2 retries)
      if (!originalRequest._retry) {
        originalRequest._retry = true;
        originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;
        
        if (originalRequest._retryCount <= 2) {
          console.log(`Retrying request... (${originalRequest._retryCount}/2)`);
          
          // Wait before retry (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, 1000 * originalRequest._retryCount));
          
          return axios(originalRequest);
        }
      }
      
      // All retries failed
      toast.error('İnternet bağlantınızı kontrol edin');
      return Promise.reject(error);
    }
    
    // Server errors
    const status = error.response.status;
    const errorMessage = error.response.data?.detail || error.response.data?.message || error.message;
    
    // Handle specific status codes
    switch (status) {
      case 400:
        // Bad request - show specific error
        if (!originalRequest.url?.includes('/chat/stream')) {
          toast.error(errorMessage || 'Geçersiz istek');
        }
        break;
        
      case 401:
        // Unauthorized - redirect to login (handled by components)
        console.log('Unauthorized request');
        break;
        
      case 403:
        toast.error('⛔ Bu işlem için yetkiniz yok');
        break;
        
      case 404:
        // Exclude specific endpoints that handle their own 404 errors
        if (!originalRequest.url?.includes('/chat/verification') && 
            !originalRequest.url?.includes('/parent/approve-link')) {
          toast.error('📂 İstenen kaynak bulunamadı');
        }
        break;
        
      case 429:
        toast.error('Çok fazla istek gönderdiniz. Lütfen biraz bekleyin.');
        break;
        
      case 500:
      case 502:
      case 503:
      case 504:
        // Server errors - retry once after delay
        if (!originalRequest._serverRetry) {
          originalRequest._serverRetry = true;
          
          console.log('Server error, retrying in 2 seconds...');
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          return axios(originalRequest);
        }
        
        toast.error('Sunucu geçici olarak kullanılamıyor. Lütfen tekrar deneyin.');
        break;
        
      default:
        // Unknown error
        console.error('API Error:', status, errorMessage);
        if (!originalRequest.url?.includes('/chat/stream')) {
          toast.error(`❌ Bir hata oluştu: ${errorMessage}`);
        }
    }
    
    return Promise.reject(error);
  }
);

// Timeout error handler
axios.defaults.timeout = 30000;

// Enable credentials for all requests (for cookie-based auth)
axios.defaults.withCredentials = true;

export default axios;
